import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  LinearProgress,
  Paper,
  IconButton,
  TextField,
  MenuItem
} from '@mui/material';
import {
  CloudUpload,
  Cancel,
  Save,
  AttachFile,
  Delete as DeleteIcon
} from '@mui/icons-material';

interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, documentType: string) => Promise<void>;
  defaultDocumentType?: string;
}

interface FileWithPreview {
  file: File;
  id: string;
  documentType: string;
}

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.gif', '.txt'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const DOCUMENT_TYPES = [
  { value: 'General', label: 'General' },
  { value: 'Quote', label: 'Quote' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Photo', label: 'Photo' },
  { value: 'Report', label: 'Report' }
];

// Preset document types for compliance documents (see specs/006-compliance-document-storage-extension).
// Not yet consumed by any component — ready for the compliance page (#100) to import.
export const COMPLIANCE_DOCUMENT_TYPES = [
  { value: 'Certificate', label: 'Certificate' },
  { value: 'Inspection Report', label: 'Inspection Report' },
  { value: 'Test Results', label: 'Test Results' },
  { value: 'Training Record', label: 'Training Record' },
  { value: 'Checklist', label: 'Checklist' },
  { value: 'Photo Evidence', label: 'Photo Evidence' },
  { value: 'Vendor Report', label: 'Vendor Report' },
  { value: 'Policy Document', label: 'Policy Document' }
];

const isSupportedFileType = (fileName: string): boolean => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return ALLOWED_EXTENSIONS.includes(extension);
};

const isValidFileSize = (fileSize: number): boolean => {
  return fileSize <= MAX_FILE_SIZE;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({
  open,
  onClose,
  onUpload,
  defaultDocumentType = 'General'
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = (files: File[]) => {
    const validFiles: FileWithPreview[] = [];
    let hasErrors = false;

    files.forEach(file => {
      if (!isValidFileSize(file.size)) {
        setError(`File "${file.name}" exceeds the 10MB size limit.`);
        hasErrors = true;
        return;
      }

      if (!isSupportedFileType(file.name)) {
        setError(`File "${file.name}" has an unsupported format. Allowed formats: ${ALLOWED_EXTENSIONS.join(', ')}`);
        hasErrors = true;
        return;
      }

      validFiles.push({
        file,
        id: `${Date.now()}-${Math.random()}`,
        documentType: defaultDocumentType
      });
    });

    if (!hasErrors) {
      setError(null);
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    processFiles(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files || []);
    processFiles(files);
  };

  const handleRemoveFile = (fileId: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleFileDocumentTypeChange = (fileId: string, newType: string) => {
    setSelectedFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, documentType: newType } : f
    ));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const totalFiles = selectedFiles.length;

      for (let i = 0; i < selectedFiles.length; i++) {
        const fileWithPreview = selectedFiles[i];
        await onUpload(fileWithPreview.file, fileWithPreview.documentType);
        setUploadProgress(((i + 1) / totalFiles) * 100);
      }

      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Object && 'data' in err && (err as { data?: { message?: string } }).data?.message
        ? String((err as { data: { message: string } }).data.message)
        : 'Failed to upload documents. Please try again.';
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setError(null);
      setUploadProgress(0);
      setIsDragOver(false);
      onClose();
    }
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #3b82f6',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2.5,
        fontWeight: 600,
        color: '#1e293b'
      }}>
        <CloudUpload sx={{ color: '#3b82f6', fontSize: 28 }} />
        Upload Documents
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc' }}>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
              Select Documents to Upload
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280', mb: 2, display: 'block' }}>
              Documents will default to "{defaultDocumentType}" type. You can change the type for
              each file individually after selecting.
            </Typography>

            <Paper
              sx={{
                border: isDragOver ? '2px dashed #3b82f6' : '2px dashed #d1d5db',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: isDragOver ? '#eff6ff' : 'transparent',
                '&:hover': {
                  borderColor: '#3b82f6',
                  backgroundColor: '#f0f9ff'
                }
              }}
              onClick={handleBrowseFiles}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CloudUpload sx={{ fontSize: 48, color: isDragOver ? '#3b82f6' : '#9ca3af', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: isDragOver ? '#3b82f6' : '#374151' }}>
                {isDragOver ? 'Drop files here' : 'Click to browse files'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                {isDragOver ? 'Release to add files' : 'or drag and drop files here'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Supported formats: {ALLOWED_EXTENSIONS.join(', ')} • Max size: 10MB per file
              </Typography>
            </Paper>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept={ALLOWED_EXTENSIONS.join(',')}
            />
          </Box>

          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                Selected Files ({selectedFiles.length})
              </Typography>

              <Stack spacing={1.5}>
                {selectedFiles.map((fileWithPreview) => (
                  <Paper
                    key={fileWithPreview.id}
                    sx={{
                      p: 2,
                      border: '1px solid #e5e7eb',
                      borderRadius: 1.5
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                      <AttachFile sx={{ color: '#6b7280' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {fileWithPreview.file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {formatFileSize(fileWithPreview.file.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(fileWithPreview.id)}
                        disabled={isUploading}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Document Type"
                      value={fileWithPreview.documentType}
                      onChange={(e) => handleFileDocumentTypeChange(fileWithPreview.id, e.target.value)}
                      disabled={isUploading}
                      sx={{
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                        }
                      }}
                    >
                      {DOCUMENT_TYPES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {isUploading && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#374151' }}>
                Uploading documents...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#3b82f6'
                  }
                }}
              />
              <Typography variant="caption" sx={{ color: '#6b7280', mt: 1 }}>
                {Math.round(uploadProgress)}% complete
              </Typography>
            </Box>
          )}

          <Alert
            severity="info"
            sx={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              '& .MuiAlert-icon': { color: '#0284c7' }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Upload guidelines:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              • Documents will be stored securely in Azure blob storage
              • Supported formats: PDF, Word, Excel, Images, and Text files
              • Maximum file size: 10MB per document
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        borderTop: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        gap: 1
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={isUploading}
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <Save />}
          disabled={selectedFiles.length === 0 || isUploading}
          sx={{
            backgroundColor: '#162346',
            '&:hover': {
              backgroundColor: '#1a2a52'
            }
          }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentDialog;
